import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState, forwardRef } from 'react';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { components } from 'react-select';

const Select = dynamic(() => import('react-select'), {
  ssr: false,
});

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledComparingTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 767px;
  .compare-table-select-container {
    .compare-table-select__control {
      border: none;
      cursor: pointer;

      .compare-table-select__indicator-separator {
        display: none;
      }

      .compare-table-select__value-container {
        padding: 0;

        .compare-table-select__input-container {
          padding: 0;
          margin: 0;
        }

        .compare-table-select__single-value {
          font-family: 'Aeonik';
          font-style: normal;
          font-weight: 500;
          font-size: ${fontSizes.large2X}px;
          line-height: 150%;
          color: ${colors.gray[800]};
        }
      }

      &.compare-table-select__control--is-focused {
        box-shadow: none;
      }
    }

    .compare-table-select__menu {
      min-width: 200px;
      right: 0;

      .compare-table-select__menu-list {
        .compare-table-select__option {
          font-family: 'Aeonik';
          font-style: normal;
          font-weight: 500;
          font-size: ${fontSizes.large2X}px;
          line-height: 150%;
          color: ${colors.gray[800]};

          cursor: pointer;

          &.compare-table-select__option--is-focused {
            background-color: ${colors.indigo[200]};
          }

          &.compare-table-select__option--is-selected {
            background-color: ${colors.indigo[500]};
          }
        }
      }
    }
  }

  @media ${devices.tablet} {
    .compare-table-select-container {
      .compare-table-select__control {
        .compare-table-select__value-container {
          .compare-table-select__single-value {
            font-size: ${fontSizes.small}px;
          }
        }

        .compare-table-select__indicators {
          .compare-table-select__indicator {
            &.compare-table-select__dropdown-indicator {
              position: absolute;
              top: -30px;
              left: calc(50% - 20px);
            }
          }
        }
      }

      .compare-table-select__menu {
        .compare-table-select__menu-list {
          .compare-table-select__option {
            font-size: ${fontSizes.small}px;
          }
        }
      }
    }
  }
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;

  &:first-child {
    background: none;
    *:first-child {
      border-top-left-radius: 20px;
    }
    *:last-child {
      border-top-right-radius: 20px;
    }
  }
  &:last-child {
    *:first-child {
      border-bottom-left-radius: 20px;
    }
    *:last-child {
      border-bottom-right-radius: 20px;
    }
  }
  @media ${devices.tablet} {
    &:first-child {
      *:first-child {
        border-top-left-radius: 0px;
      }
      *:last-child {
        border-top-right-radius: 0px;
      }
    }
    &:last-child {
      *:first-child {
        border-bottom-left-radius: 0px;
      }
      *:last-child {
        border-bottom-right-radius: 0px;
      }
    }
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCol = styled(
  forwardRef(
    (
      {
        borderBottom,
        borderLeft,
        borderRight,
        centered,
        width,
        background,
        ...props
      },
      ref
    ) => <div ref={ref} {...props} />
  )
)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-grow: 1;
  padding-top: ${spaces.regular}px;
  padding-bottom: ${spaces.regular}px;
  padding-left: ${spaces.medium}px;
  padding-right: ${spaces.medium - 2}px;
  width: 30%;

  ${({ borderBottom }) =>
    borderBottom &&
    css`
      border-bottom: 1px solid ${colors.gray[200]};
    `}
  ${({ background }) =>
    background &&
    css`
      background: ${background};
    `}
  ${({ borderLeft }) =>
    borderLeft &&
    css`
      border-left: 1px solid ${colors.gray[200]};
    `}

  ${({ borderRight }) =>
    borderRight &&
    css`
      border-right: 1px solid ${colors.gray[200]};
    `}

  ${({ centered }) =>
    centered &&
    css`
      justify-content: center;
    `}

  &:first-child {
    width: 36%;
  }

  @media ${devices.tablet} {
    width: 115px;
    padding-top: ${spaces.small2X}px;
    padding-bottom: ${spaces.small2X}px;
    padding-left: ${spaces.small - 2}px;
    padding-right: ${spaces.small - 2}px;
    &:first-child {
      width: calc(100% - 230px);
    }
  }
`;
// eslint-disable-next-line prettier/prettier, react/display-name
const StyledColHeader = styled(
  forwardRef(
    (
      {
        borderBottom,
        borderLeft,
        borderRight,
        centered,
        width,
        background,
        ...props
      },
      ref
    ) => <div ref={ref} {...props} />
  )
)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-grow: 1;

  width: 30%;

  ${({ borderBottom }) =>
    borderBottom &&
    css`
      border-bottom: 1px solid ${colors.gray[200]};
    `}
  ${({ background }) =>
    background &&
    css`
      background: ${background};
    `}
  ${({ borderLeft }) =>
    borderLeft &&
    css`
      border-left: 1px solid ${colors.gray[200]};
    `}

  ${({ borderRight }) =>
    borderRight &&
    css`
      border-right: 1px solid ${colors.gray[200]};
    `}

  ${({ centered }) =>
    centered &&
    css`
      justify-content: center;
    `}

  &:first-child {
    width: 36%;
  }

  @media ${devices.tablet} {
    width: 115px;

    &:first-child {
      width: calc(100% - 230px);
    }
  }
`;
// eslint-disable-next-line prettier/prettier, react/display-name
const StyledIconContainer = styled(
  forwardRef(({ desktop, mobile, centered, rightSpace, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  height: 100%;

  ${({ centered }) =>
    centered &&
    css`
      align-items: center;
    `}

  ${({ rightSpace }) =>
    rightSpace &&
    css`
      margin-right: ${spaces.medium}px;
    `}

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    ${({ rightSpace }) =>
      rightSpace &&
      css`
        margin-right: ${spaces.small}px;
      `}

    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;
const StyledFirstTitle = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  color: ${colors.gray[800]};
  padding: ${spaces.medium}px;
  @media ${devices.tablet} {
    font-size: ${fontSizes.small}px;
    text-align: center;
    width: 100%;
  }
`;
const StyledTitle = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  color: ${colors.white};
  padding-left: ${spaces.small - 2}px;
  padding-right: ${spaces.small - 2}px;
  padding-top: ${spaces.small}px;
  padding-bottom: ${spaces.small}px;
  text-align: center;
  @media ${devices.tablet} {
    font-size: ${fontSizes.small}px;
    font-weight: 400;
    padding-left: ${spaces.small2X}px;
    padding-right: ${spaces.small2X}px;
    padding-top: ${spaces.small2X}px;
    padding-bottom: ${spaces.small2X}px;
  }
`;

const StyledText = styled.div`
  margin-bottom: ${spaces.smallX}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 130%;
  color: ${colors.gray[700]};
  @media ${devices.tablet} {
    font-size: ${fontSizes.small}px;
  }
`;

const StyledSubtext = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.regular}px;
  line-height: 130%;
  color: ${colors.gray[700]};

  @media ${devices.tablet} {
    font-size: ${fontSizes.smallX}px;
  }
`;

const StyledItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ComparingTable = ({ competitors = [], items = [] }) => {
  const [activeOption, setActiveOption] = useState(null);
  const [options, setOptions] = useState([]);

  const handleChange = useCallback((option) => {
    setActiveOption(option);
  }, []);

  useEffect(() => {
    setOptions(
      competitors.slice(1).map(({ key, title }) => {
        return { value: key, label: title };
      })
    );
  }, [competitors]);

  useEffect(() => {
    if (options.length) {
      setActiveOption(options[0]);
    }
  }, [options]);

  const rows = useMemo(() => {
    const standardData = competitors[0];
    const competitorData = competitors.find(
      ({ key }) => key === activeOption?.value
    );

    return items.map(({ key, title, description, iconAlias, iconColor }) => {
      return {
        key,
        title,
        description,
        iconAlias,
        iconColor,
        standard: standardData?.itemKeys?.includes(key),
        competitor: competitorData?.itemKeys?.includes(key),
      };
    });
  }, [items, competitors, activeOption]);

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <Icon
          alias={ICONS_ALIASES.chevronDownRounded}
          color={colors.gray[800]}
        />
      </components.DropdownIndicator>
    );
  };
  return (
    <StyledComparingTable>
      <StyledRow>
        <StyledColHeader borderBottom background={colors.gray[200]}>
          <StyledFirstTitle>BENEFIT</StyledFirstTitle>
        </StyledColHeader>
        <StyledColHeader
          borderBottom
          centered
          background="linear-gradient(0deg, #039855 0%, #6CE9A6 100%);"
        >
          <StyledTitle>{competitors[0].title}</StyledTitle>
        </StyledColHeader>
        <StyledColHeader
          borderBottom
          centered
          background="linear-gradient(45deg, #3538CD 0.9%, #8098F9 86.82%);"
        >
          {options.length > 1 && (
            <Select
              className="compare-table-select-container"
              classNamePrefix="compare-table-select"
              components={{ DropdownIndicator }}
              isSearchable={false}
              options={options}
              placeholder=""
              value={activeOption}
              onChange={handleChange}
            />
          )}
          {options.length === 1 && (
            <StyledTitle>{options[0]?.label}</StyledTitle>
          )}
        </StyledColHeader>
      </StyledRow>
      {rows.map(
        ({
          key,
          title,
          description,
          iconAlias,
          iconColor,
          standard,
          competitor,
        }) => (
          <StyledRow key={key}>
            <StyledCol borderBottom borderLeft borderRight>
              {iconAlias && (
                <>
                  <StyledIconContainer desktop centered rightSpace>
                    <Icon alias={iconAlias} color={iconColor} />
                  </StyledIconContainer>
                  <StyledIconContainer mobile rightSpace>
                    <Icon
                      alias={iconAlias}
                      color={iconColor}
                      width={24}
                      height={24}
                    />
                  </StyledIconContainer>
                </>
              )}
              <StyledItemContainer>
                <StyledText>{title}</StyledText>
                {description && <StyledSubtext>{description}</StyledSubtext>}
              </StyledItemContainer>
            </StyledCol>
            <StyledCol borderBottom borderRight centered>
              <StyledIconContainer desktop centered>
                <Icon
                  alias={
                    standard
                      ? ICONS_ALIASES.checkFilled
                      : ICONS_ALIASES.closeFilled
                  }
                  color={standard ? colors.green[500] : colors.gray[500]}
                />
              </StyledIconContainer>
              <StyledIconContainer mobile centered>
                <Icon
                  alias={
                    standard
                      ? ICONS_ALIASES.checkFilled
                      : ICONS_ALIASES.closeFilled
                  }
                  color={standard ? colors.indigo[600] : colors.gray[500]}
                  width={24}
                  height={24}
                />
              </StyledIconContainer>
            </StyledCol>
            <StyledCol borderBottom borderRight centered>
              <StyledIconContainer desktop centered>
                <Icon
                  alias={
                    competitor
                      ? ICONS_ALIASES.checkFilled
                      : ICONS_ALIASES.closeFilled
                  }
                  color={competitor ? colors.indigo[500] : colors.gray[500]}
                />
              </StyledIconContainer>
              <StyledIconContainer mobile centered>
                <Icon
                  alias={
                    competitor
                      ? ICONS_ALIASES.checkFilled
                      : ICONS_ALIASES.closeFilled
                  }
                  color={competitor ? colors.indigo[500] : colors.gray[500]}
                  width={24}
                  height={24}
                />
              </StyledIconContainer>
            </StyledCol>
          </StyledRow>
        )
      )}
    </StyledComparingTable>
  );
};

ComparingTable.propTypes = {
  competitors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.node,
      itemKeys: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.node,
      description: PropTypes.node,
      iconAlias: PropTypes.string,
      iconColor: PropTypes.string,
    })
  ),
};

export default ComparingTable;
